import { DownOutlined } from '@ant-design/icons';
import { Button, Form, Select } from 'antd';
import React from 'react';
import type { FilterMetadata } from '../types/filter-metadata.types';
import { FilterInputType } from '../types/filter-metadata.types';
import Styles from './Styles.module.scss';

type GroupOperator = 'AND' | 'OR';

interface FieldSelectorProps {
    value: string;
    onChange: (field: string) => void;
    options: any[];
    isMultiSelect?: boolean;
    multiSelectOperator?: GroupOperator;
    onMultiSelectOperatorChange?: (operator: GroupOperator) => void;
}

export const FieldSelector: React.FC<FieldSelectorProps> = ({
    value,
    onChange,
    options,
    isMultiSelect = false,
    multiSelectOperator = 'OR',
    onMultiSelectOperatorChange,
}) => {
    // Helper function to safely convert to string
    const safeToString = (val: any): string => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string') return val;
        if (typeof val === 'number' || typeof val === 'boolean') return String(val);
        // For objects, try to get label or value property
        if (typeof val === 'object') {
            if ('label' in val && typeof val.label === 'string') return val.label;
            if ('value' in val && typeof val.value === 'string') return val.value;
        }
        return '';
    };

    const filterOption = (input: string, option: any): boolean => {
        // For grouped options, search in both label and options
        const searchText = input.toLowerCase();

        if (option?.label) {
            // Check if label (category) matches
            const labelStr = safeToString(option.label);
            if (labelStr.toLowerCase().includes(searchText)) {
                return true;
            }
        }
        // Check if any option in the group matches
        if (option?.options && Array.isArray(option.options)) {
            return option.options.some((opt: any) => {
                const optLabel = safeToString(opt?.label);
                const optValue = safeToString(opt?.value);
                return optLabel.toLowerCase().includes(searchText) || optValue.toLowerCase().includes(searchText);
            });
        }
        // Check individual option
        const optionLabel = safeToString(option?.label);
        const optionValue = safeToString(option?.value);
        return optionLabel.toLowerCase().includes(searchText) || optionValue.toLowerCase().includes(searchText);
    };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <Select
                value={value}
                onChange={onChange}
                options={options as any}
                className={Styles.customSelect}
                showSearch
                suffixIcon={<DownOutlined />}
                filterOption={filterOption}
                placeholder="Select filter field"
            />
            {isMultiSelect && (
                <div
                    style={{
                        position: 'absolute',
                        right: '24px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'flex',
                        alignItems: 'center',
                        zIndex: 10,
                        pointerEvents: 'auto',
                    }}
                    onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();
                    }}
                >
                    <Button
                        color={multiSelectOperator === 'AND' ? 'blue' : 'green'}
                        className={Styles.logicalOperatorButton}
                        onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            onMultiSelectOperatorChange?.(multiSelectOperator === 'AND' ? 'OR' : 'AND');
                        }}
                    >
                        {multiSelectOperator}
                    </Button>
                </div>
            )}
        </div>
    );
};
